import { PageLayout } from '@/layouts/page-layout'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useGetAssetsV2Query } from '@/store/services'
import { CreateButton, DownloadButton, MassiveUploadButton } from '@/shared/components/action-buttons'
import { AssetV2Table } from './tables/asset-v2.table'
import DebouncedInput from '@/shared/components/debounced-input'
import { FilterPopover } from '@/shared/components/filter-popover'
import { AssetCategorySelector, AssetTypeSelector, AccreditationSelector, ContractSelector, StatusSelector } from '@/shared/selectors'
import { LinkWithQuery } from '@/shared/components/link-with-query'
import { HasAccess } from '@permify/react-role'

export const AssetV2List = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedCategoryParam = searchParams.get('asset_category_id')
  const selectedTypeParam = searchParams.get('asset_type_id')
  const accreditedParam = searchParams.get('accredited')
  const contractParam = searchParams.get('contract_id')
  const statusParam = searchParams.get('status')
  const [categoryFilter, setCategoryFilter] = useState(selectedCategoryParam ? Number(selectedCategoryParam) : null)
  const [typeFilter, setTypeFilter] = useState(selectedTypeParam ? Number(selectedTypeParam) : null)
  const [contractFilter, setContractFilter] = useState(contractParam ? Number(contractParam) : null)
  const [statusFilter, setStatusFilter] = useState(statusParam || null)

  useEffect(() => {
    setCategoryFilter(selectedCategoryParam ? Number(selectedCategoryParam) : null)
  }, [selectedCategoryParam])

  useEffect(() => {
    setTypeFilter(selectedTypeParam ? Number(selectedTypeParam) : null)
  }, [selectedTypeParam])

  useEffect(() => {
    setContractFilter(contractParam ? Number(contractParam) : null)
  }, [contractParam])

  useEffect(() => {
    setStatusFilter(statusParam || null)
  }, [statusParam])

  useEffect(() => {
    if (!searchParams.get('page') || !searchParams.get('per_page')) {
      const next = new URLSearchParams(searchParams)
      if (!next.get('page')) {
        next.set('page', 1)
      }
      if (!next.get('per_page')) {
        next.set('per_page', 10)
      }
      setSearchParams(next)
    }
  }, [searchParams, setSearchParams])

  const handleParam = (name, value) => {
    const next = new URLSearchParams(searchParams)
    if (value === null || value === undefined || value === '') {
      next.delete(name)
    } else {
      next.set(name, value)
    }
    next.set('page', 1)
    if (!next.get('per_page')) {
      next.set('per_page', 10)
    }
    setSearchParams(next)
  }

  const resetFilters = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('asset_category_id')
    next.delete('asset_type_id')
    next.delete('q')
    next.delete('accredited')
    next.delete('contract_id')
    next.delete('status')
    next.set('page', 1)
    if (!next.get('per_page')) {
      next.set('per_page', 10)
    }
    setSearchParams(next)
  }

  const queryString = useMemo(() => {
    const params = new URLSearchParams(searchParams)
    if (categoryFilter) {
      params.set('asset_category_id', categoryFilter)
    } else {
      params.delete('asset_category_id')
    }
    if (typeFilter) {
      params.set('asset_type_id', typeFilter)
    } else {
      params.delete('asset_type_id')
    }
    if (contractFilter) {
      params.set('contract_id', contractFilter)
    } else {
      params.delete('contract_id')
    }
    if (statusFilter) {
      params.set('status', statusFilter)
    } else {
      params.delete('status')
    }
    return params.toString()
  }, [searchParams, categoryFilter, typeFilter, contractFilter, statusFilter])

  const { data: { assets = [], meta = {} } = {}, isFetching } = useGetAssetsV2Query(queryString)

  return (
    <PageLayout
      title='Activos'
      actions={
        <div className='flex gap-3 items-center'>
          <div className='w-80'>
            <DebouncedInput
              name='q'
              label='Buscar por identificador o RUT'
              value={searchParams.get('q') || ''}
              onChange={(value) => handleParam('q', value)}
            />
          </div>
          <FilterPopover>
            <div className='grid gap-4 min-w-[260px]'>
              <div className='grid gap-2'>
                <p className='text-sm font-medium text-gray-700'>Categoría</p>
                <AssetCategorySelector
                  key='asset-category-selector'
                  includeAll
                  value={categoryFilter}
                  onChange={(val) => {
                    setCategoryFilter(val ?? null)
                    setTypeFilter(null)
                    const next = new URLSearchParams(searchParams)
                    if (val) {
                      next.set('asset_category_id', val)
                    } else {
                      next.delete('asset_category_id')
                    }
                    next.delete('asset_type_id')
                    next.set('page', 1)
                    if (!next.get('per_page')) {
                      next.set('per_page', 10)
                    }
                    setSearchParams(next)
                  }}
                />
              </div>
              <div className='grid gap-2'>
                <p className='text-sm font-medium text-gray-700'>Tipo</p>
                <AssetTypeSelector
                  key={`asset-type-selector-${categoryFilter || 'all'}`}
                  includeAll
                  categoryId={categoryFilter}
                  value={typeFilter}
                  onChange={(val) => {
                    setTypeFilter(val ?? null)
                    const next = new URLSearchParams(searchParams)
                    if (val) {
                      next.set('asset_type_id', val)
                    } else {
                      next.delete('asset_type_id')
                    }
                    next.set('page', 1)
                    if (!next.get('per_page')) {
                      next.set('per_page', 10)
                    }
                    setSearchParams(next)
                  }}
                />
              </div>
              <div className='grid gap-2'>
                <p className='text-sm font-medium text-gray-700'>Contrato</p>
                <ContractSelector
                  value={contractFilter}
                  onChange={(option) => {
                    const val = option?.value || null
                    setContractFilter(val)
                    const next = new URLSearchParams(searchParams)
                    if (val) {
                      next.set('contract_id', val)
                    } else {
                      next.delete('contract_id')
                    }
                    next.set('page', 1)
                    if (!next.get('per_page')) {
                      next.set('per_page', 10)
                    }
                    setSearchParams(next)
                  }}
                />
              </div>
              <div className='grid gap-2'>
                <p className='text-sm font-medium text-gray-700'>Estado</p>
                <StatusSelector
                  value={statusFilter}
                  onChange={({ value }) => {
                    setStatusFilter(value || null)
                    handleParam('status', value || '')
                  }}
                />
              </div>
              <div className='grid gap-2'>
                <p className='text-sm font-medium text-gray-700'>Acreditado</p>
                <AccreditationSelector
                  value={accreditedParam || ''}
                  onChange={(opt) => handleParam('accredited', opt?.value || '')}
                />
              </div>
              <div className='flex justify-center mt-2'>
                <button
                  type='button'
                  className='btn btn-outline btn-sm text-red-600 border-red-200'
                  onClick={resetFilters}
                >
                  {t('filters.delete')}
                </button>
              </div>
            </div>
          </FilterPopover>
          <HasAccess permissions='asset_v2#upload_attachments'>
            <Link to='import'>
              <MassiveUploadButton />
            </Link>
          </HasAccess>
          {(typeFilter || categoryFilter) && (
            <LinkWithQuery to='reports'>
              <DownloadButton />
            </LinkWithQuery>
          )}
          {!typeFilter && !categoryFilter && (
            <span className='inline-block opacity-60' title='Selecciona categoría (y opcionalmente tipo) para descargar'>
              <div className='pointer-events-none'>
                <DownloadButton />
              </div>
            </span>
          )}
          <HasAccess permissions={['asset_v2#create']}>
            <Link to='create'>
              <CreateButton />
            </Link>
          </HasAccess>
        </div>
      }
    >
      <Outlet />
      <AssetV2Table assets={assets} meta={meta} loading={isFetching} />
    </PageLayout>
  )
}
